<script lang="ts" setup>
interface NavigationLevel {
  label: string
  Title?: string
  Link?: string
  url?: string
  Banner1?: unknown
  Banner2?: unknown
  Subnavigation?: NavigationSubLevel[]
}

interface NavigationSubLevel {
  Title: string
  Link?: string
  AllTitle?: string
  Show?: boolean
  Subnavigation?: NavigationSubLevel[]
}

const props = defineProps<{
  item: NavigationLevel
}>()

const firstSubnavigation = computed(() => props.item?.Subnavigation?.[0] ?? null)
const secondSubnavigation = computed(() => props.item?.Subnavigation?.[1] ?? null)
const remainingSubnavigation = computed(() => {
  return Array.isArray(props.item?.Subnavigation)
    ? props.item.Subnavigation.slice(2)
    : []
})

const showSubmenu = ref<number | null>(null)

function openSubnavigation(index: number) {
  showSubmenu.value = index
}

function closeSubnavigation(index: number) {
  if (showSubmenu.value === index) {
    showSubmenu.value = null
  }
}

function toggleSubnavigation(index: number) {
  if (showSubmenu.value === index) {
    closeSubnavigation(index)
  }
  else {
    openSubnavigation(index)
  }
}
</script>

<template>
  <div class="subnavigation__content--columns">
    <div
      v-if="firstSubnavigation"
      class="subnavigation__links subnavigation__links--1"
    >
      <button
        v-if="firstSubnavigation?.Subnavigation?.length"
        type="button"
        class="subnavigation__link"
        aria-haspopup="true"
        :aria-expanded="showSubmenu === 0 ? 'true' : 'false'"
        @click="toggleSubnavigation(0)"
      >
        <strong>{{ firstSubnavigation.Title }}</strong>
        <Icon name="icon:arrow" />
      </button>
      <AppLink
        v-else-if="firstSubnavigation?.Link"
        :to="firstSubnavigation?.Link"
        class="subnavigation__link"
      >
        <strong>{{ firstSubnavigation?.Title }}</strong>
      </AppLink>

      <div
        class="subnavigation__content"
        :class="{ 'subnavigation--open': 0 === showSubmenu }"
      >
        <div class="subnavigation__container">
          <div class="subnavigation__header">
            <button
              type="button"
              class="subnavigation__back"
              @click="toggleSubnavigation(0)"
            >
              <Icon
                name="icon:arrow"
                class="icon--arrow-left"
              />
              <span>{{ firstSubnavigation?.Title }}</span>
            </button>
          </div>

          <ul>
            <template v-for="(menuItem, index) in firstSubnavigation?.Subnavigation">
              <li
                v-if="menuItem?.Title && menuItem?.Link && menuItem?.Show"
                :key="index"
                class="subnavigation__item"
              >
                <AppLink
                  :to="menuItem?.Link"
                  class="subnavigation__link"
                >
                  <span>{{ menuItem?.Title }}</span>
                </AppLink>
              </li>
            </template>
          </ul>

          <AppLink
            v-if="firstSubnavigation?.AllTitle && firstSubnavigation?.Link"
            :to="firstSubnavigation?.Link"
            class="subnavigation__title"
          >
            <strong>{{ firstSubnavigation.AllTitle }}</strong>
          </AppLink>
        </div>
      </div>
    </div>

    <div
      v-if="secondSubnavigation"
      class="subnavigation__links subnavigation__links--2"
    >
      <button
        v-if="secondSubnavigation?.Subnavigation?.length"
        type="button"
        class="subnavigation__link"
        aria-haspopup="true"
        :aria-expanded="showSubmenu === 1 ? 'true' : 'false'"
        @click="toggleSubnavigation(1)"
      >
        <strong>{{ secondSubnavigation.Title }}</strong>
        <Icon name="icon:arrow" />
      </button>
      <AppLink
        v-else-if="secondSubnavigation?.Link"
        :to="secondSubnavigation?.Link"
        class="subnavigation__link"
      >
        <strong>{{ secondSubnavigation?.Title }}</strong>
      </AppLink>

      <div
        class="subnavigation__content"
        :class="{ 'subnavigation--open': 1 === showSubmenu }"
      >
        <div class="subnavigation__container">
          <div class="subnavigation__header">
            <button
              type="button"
              class="subnavigation__back"
              @click="toggleSubnavigation(1)"
            >
              <Icon
                name="icon:arrow"
                class="icon--arrow-left"
              />
              <span>{{ secondSubnavigation?.Title }}</span>
            </button>
          </div>

          <ul>
            <template v-for="(menuItem, index) in secondSubnavigation?.Subnavigation">
              <li
                v-if="menuItem?.Title && menuItem?.Link && menuItem?.Show"
                :key="index"
                class="subnavigation__item"
              >
                <AppLink
                  :to="menuItem?.Link"
                  class="subnavigation__link"
                >
                  <span>{{ menuItem?.Title }}</span>
                </AppLink>
              </li>
            </template>
          </ul>

          <AppLink
            v-if="secondSubnavigation?.AllTitle && secondSubnavigation?.Link"
            :to="secondSubnavigation?.Link"
            class="subnavigation__title"
          >
            <strong>{{ secondSubnavigation.AllTitle }}</strong>
          </AppLink>
        </div>
      </div>
    </div>

    <div
      v-if="remainingSubnavigation"
      class="subnavigation__links subnavigation__links--3"
    >
      <ul>
        <template v-for="(menuItem, index) in remainingSubnavigation">
          <li
            v-if="menuItem?.Title && menuItem?.Link && menuItem?.Show"
            :key="index"
            class="subnavigation__item"
          >
            <AppLink
              :to="menuItem?.Link"
              class="subnavigation__link"
            >
              <strong>{{ menuItem?.Title }}</strong>
            </AppLink>
          </li>
        </template>
      </ul>
    </div>

    <div class="subnavigation__impression">
      <NavigationBanner
        v-if="item?.Banner1 && !item?.Banner2"
        :banner="item.Banner1"
      />

      <template v-else>
        <NavigationBannerSmall
          v-if="item?.Banner1"
          :banner="item.Banner1 "
        />

        <NavigationBannerSmall
          v-if="item?.Banner2"
          :banner="item.Banner2"
        />
      </template>
    </div>
  </div>
</template>

<style>
@import url("~layers/app/components/Navigation/Level2Content.css");
</style>
